import React, { useEffect, useState } from 'react';
import { CircularProgress, Stack, styled } from '@mui/material';
import NetworkError from 'assets/images/ErrorState/NetworkError.svg';
import { ResourceCareProgramTopicSection } from 'components/Resources/ResourceCareProgramTopicSection';
import { ResourceLibraryArticleSection } from 'components/Resources/ResourceLibraryArticleSection';
import { ResourceBenefitSection } from 'components/Resources/ResourceBenefitSection';
import { ContainerLayout, HeaderBar, ScrollableContainer } from 'components/index';
import { ErrorState } from 'components/ErrorState/ErrorState';
import { Alert } from 'components/Alert/Alert';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import {
  useBenefits,
  useResources,
  useLibraryArticles,
  useLibraryArticleFavorites,
} from 'features/resources/hooks';
import { ArticleView } from 'components/ArticleView/ArticleView';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import { toLibraryArticleFavoriteRequest } from 'features/resources/utils/libraryArticleFavoriteUtils';
import { TransformedLibraryArticle } from 'features/resources/types';
import { RecommendedCareProgram } from 'components/Resources/RecommendedCareProgram';
import { usePendo } from 'features/pendo/hooks/usePendo';
import { PendoEventType } from 'features/pendo/types';

const ContentStack = styled(Stack)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(12),
  [theme.breakpoints.up('sm')]: {
    paddingTop: theme.spacing(5),
  },
  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(7),
  },
}));

const CenteredState = styled(Stack)(() => ({
  height: '100%',
  justifyContent: 'center',
  alignItems: 'center',
}));

export const ResourceFeature = () => {
  const { translation } = useTranslationContext();
  const {
    isLoadingHealthTopics,
    healthTopicsError,
    refetchHealthTopics,
    alertState,
    dismissAlert,
    activeArticle,
    handleModalClose,
    handleArticleClick,
  } = useResources();
  const {
    libraryArticles,
    isLoadingLibraryArticles,
    isFetchingLibraryArticles,
    hasMoreLibraryArticles,
    loadMoreLibraryArticles,
    isLoadMoreError,
    isLibraryError,
    refetchLibraryArticles,
  } = useLibraryArticles();
  const isLoading = isLoadingHealthTopics || isLoadingLibraryArticles;
  const isResourceError = !!healthTopicsError || isLibraryError;
  const refetch = () => {
    refetchHealthTopics();
    refetchLibraryArticles();
  };
  const { isDesktop } = useDeviceType();
  const {
    favoritedArticles,
    isFavorited: isArticleFavorited,
    toggleFavorite,
    loadFavorites,
  } = useLibraryArticleFavorites(libraryArticles);
  const { trackEvent } = usePendo();
  const [isFavoriteToastPresented, setIsFavoriteToastPresented] = useState(false);

  // Lazy query: fetch favorites once on mount (no auto-fetch without tags).
  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  const handleToggleFavorite = async (article: TransformedLibraryArticle) => {
    try {
      const result = await toggleFavorite(toLibraryArticleFavoriteRequest(article));
      if (result === 'added') {
        trackEvent(PendoEventType.FAVORITE_ARTICLE, {
          eventType: PendoEventType.FAVORITE_ARTICLE,
          articleId: `${article.id}`,
        });
        setIsFavoriteToastPresented(true);
      } else {
        trackEvent(PendoEventType.REMOVED_FAVORITE_ARTICLE, {
          eventType: PendoEventType.REMOVED_FAVORITE_ARTICLE,
          articleId: `${article.id}`,
        });
      }
    } catch (error) {
      // Favorite failed; no toast shown.
      // throwing error to be catched and rollback the local state in LibraryCard
      throw new Error((error as Error)?.message || 'Failed to toggle favorite');
    }
  };

  return (
    <ContainerLayout>
      <HeaderBar titleId="resources_screen_title" heading={translation('resources_screen_title')} />
      <ScrollableContainer role="main" aria-labelledby="resources_screen_title">
        {isLoading && (
          <CenteredState>
            <CircularProgress />
          </CenteredState>
        )}
        {isResourceError && (
          <CenteredState>
            <ErrorState
              imageArray={[NetworkError]}
              onRetryClick={refetch}
              title={translation('no_connection_overlay')}
              subtitle={translation('error_loading_body')}
            />
          </CenteredState>
        )}
        {!isLoading && !isResourceError && (
          <>
            <ContentStack direction="column" alignItems="center" spacing={6}>
              {isDesktop ? <RecommendedCareProgram /> : <ResourceCareProgramTopicSection />}
              <ResourceBenefitSection />
              <ResourceLibraryArticleSection
                libraryArticles={libraryArticles}
                hasMore={hasMoreLibraryArticles}
                isFetchingMore={isFetchingLibraryArticles}
                onLoadMore={loadMoreLibraryArticles}
                isLoadMoreError={isLoadMoreError}
                onRetryLoadMore={() => refetchLibraryArticles()}
                favoritedArticles={favoritedArticles}
                handleArticleClick={handleArticleClick}
                isArticleFavorited={(article: TransformedLibraryArticle) =>
                  isArticleFavorited(article.contentUrl)
                }
                onToggleFavorite={(article: TransformedLibraryArticle) =>
                  handleToggleFavorite(article)
                }
              />
            </ContentStack>
            {activeArticle && (
              <ArticleView
                showHeader
                showFavorite
                title={activeArticle.title}
                open
                url={activeArticle.contentUrl}
                isFavorited={isArticleFavorited(activeArticle.contentUrl)}
                onToggleFavorite={() => handleToggleFavorite(activeArticle)}
                onClose={handleModalClose}
                overrideCloseButtonVisible={isDesktop}
                overrideBackButtonVisible={!isDesktop}
                onClickBack={handleModalClose}
              />
            )}
            <Alert
              isPresented={isFavoriteToastPresented}
              handleDismiss={() => setIsFavoriteToastPresented(false)}
              message={translation('content_favorites_add')}
              presentationDuration={5000}
              variant="filled"
              icon={false}
              snackbarSx={{ marginBottom: 3.5 }}
              sx={{ backgroundColor: 'brandShade2.main' }}
            />
          </>
        )}
      </ScrollableContainer>
    </ContainerLayout>
  );
};
